import { TechnicalsPivotRow } from '../../../util/technicals'
import { TechnicalsCandleSeries } from './types'

const LEVEL_IDS = ['R3', 'R2', 'R1', 'P', 'S1', 'S2', 'S3'] as const

type Ohlc = { open: number; high: number; low: number; close: number }

const classicLevels = (h: number, l: number, c: number, range: number): number[] => {
    const p = (h + l + c) / 3
    const r1 = 2 * p - l
    const s1 = 2 * p - h
    const r2 = p + range
    const s2 = p - range
    const r3 = p + 2 * range
    const s3 = p - 2 * range
    return [r3, r2, r1, p, s1, s2, s3]
}

const fibonacciLevels = (h: number, l: number, c: number, range: number): number[] => {
    const p = (h + l + c) / 3
    return [
        p + range,
        p + 0.618 * range,
        p + 0.382 * range,
        p,
        p - 0.382 * range,
        p - 0.618 * range,
        p - range
    ]
}

const camarillaLevels = (h: number, l: number, c: number, range: number): number[] => {
    const factor = (1.1 * range) / 12
    const r1 = c + factor
    const s1 = c - factor
    const p = (r1 + s1) / 2
    return [c + 4 * factor, c + 2 * factor, r1, p, s1, c - 2 * factor, c - 4 * factor]
}

const woodieLevels = (h: number, l: number, c: number, range: number): number[] => {
    const p = (h + l + 2 * c) / 4
    const r1 = 2 * p - l
    const s1 = 2 * p - h
    return [h + 2 * (p - l), p + range, r1, p, s1, p - range, l - 2 * (h - p)]
}

const dmLevels = (h: number, l: number, c: number, o: number, range: number): number[] => {
    const x = c > o ? 2 * h + l + c : c < o ? h + 2 * l + c : h + l + 2 * c
    const p = x / 4
    const r1 = x / 2 - l
    const s1 = x / 2 - h
    return [r1 + range, p + range, r1, p, s1, p - range, s1 - range]
}

const emptyLevels = (): TechnicalsPivotRow[] =>
    LEVEL_IDS.map((id) => ({
        id: id.toLowerCase(),
        name: id,
        classic: 0,
        fibonacci: 0,
        camarilla: 0,
        woodie: 0,
        dm: 0,
        hasValue: false
    }))

export const computeTechnicalsPivots = (series: TechnicalsCandleSeries): TechnicalsPivotRow[] => {
    if (series.close.length < 2) return emptyLevels()

    const priorIdx = series.close.length - 2
    const ohlc: Ohlc = {
        open: series.open[priorIdx],
        high: series.high[priorIdx],
        low: series.low[priorIdx],
        close: series.close[priorIdx]
    }
    const { high: h, low: l, close: c, open: o } = ohlc
    const range = h - l

    if (range <= 0 || Number.isNaN(h) || Number.isNaN(l) || Number.isNaN(c)) {
        return emptyLevels()
    }

    const classic = classicLevels(h, l, c, range)
    const fibonacci = fibonacciLevels(h, l, c, range)
    const camarilla = camarillaLevels(h, l, c, range)
    const woodie = woodieLevels(h, l, c, range)
    const dm = dmLevels(h, l, c, o, range)

    return LEVEL_IDS.map((id, index) => ({
        id: id.toLowerCase(),
        name: id,
        classic: classic[index],
        fibonacci: fibonacci[index],
        camarilla: camarilla[index],
        woodie: woodie[index],
        dm: dm[index],
        hasValue: true
    }))
}
